/* eslint-disable no-console */
import { createContext, useState, useEffect, useContext } from "react";
import { toast } from "react-hot-toast";
import { ROLES } from "../constants/roles";
import { useSessionContext } from "@supabase/auth-helpers-react";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { session, isLoading: authLoading, supabaseClient } = useSessionContext();

  const [apellido, setApellido] = useState(null);
  const [correo, setCorreo] = useState(null);
  const [edificiosOk, setEdificiosOk] = useState(null);
  const [habilitado, setHabilitado] = useState(null);
  const [id, setId] = useState(null);
  const [nombre, setNombre] = useState(null);
  const [pais, setPais] = useState(null);
  const [rol, setRol] = useState(null);
  const [agencia, setAgencia] = useState(null);
  const [clienteId, setClienteId] = useState(null);
  const [, setPrevTokenVersion] = useState(0);
  const [userLoading, setUserLoading] = useState(true); // Iniciar en true para evitar race condition

  const internalLogout = async () => {
    try {
      await supabaseClient.auth.signOut();
    } catch (error) {
      console.error("Error en logout:", error);
    } finally {
      window.location.href = "/login";
    }
  };

  // Helper unificado para obtener país desde hostname
  // Retorna { nombre: "Chile", codigo: "CHI" }
  // PARA DESARROLLO: cambiar el default ACÁ afecta tanto UI como query a BD
  const getPaisFromHostname = () => {
    const hostname = window.location.hostname;
    if (hostname.includes("visiona.pe")) return { nombre: "Perú", codigo: "PER" };
    if (hostname.includes("wecast.cl")) return { nombre: "Chile", codigo: "CHI" };
    // Uruguay (Screenmedia) CONGELADO 2026-08-03 — ver regla 10.181 (relacionada 10.10).
    // El hostname screenmedia.com.uy YA NO produce sesión Uruguay: cae al default (Chile) de abajo.
    // Datos y branding de Uruguay quedan intactos; simplemente deja de haber entrada nueva por hostname.
    // if (hostname.includes("screenmedia.com.uy")) return { nombre: "Uruguay", codigo: "URU" };
    if (hostname.includes("adigitalholding.com")) return { nombre: "Region", codigo: "Region" };
    return { nombre: "Chile", codigo: "CHI" };
  };

  // FUNCIÓN ÚNICA para cargar datos del usuario (la definimos afuera)
  const loadUserData = async () => {
    // Sin sesión → limpiar todo
    if (!session?.user?.email) {
      setApellido(null);
      setCorreo(null);
      setEdificiosOk(null);
      setAgencia(null);
      setClienteId(null);
      setHabilitado(null);
      setId(null);
      setNombre(null);
      setRol(null);
      setPrevTokenVersion(0);
      setUserLoading(false); // Sin sesión, no hay nada que cargar
      return;
    }

    setUserLoading(true);
    try {
      // Obtener código de país para la query (CHI, PER, etc.)
      const currentPais = getPaisFromHostname().codigo;

      // Query filtrando por email Y país para soportar usuarios duplicados
      const { data, error } = await supabaseClient
        .from("usuarios")
        .select("*")
        .eq("email", session.user.email)
        .eq("pais", currentPais)
        .single();

      if (error || !data) throw new Error("Usuario no encontrado para este país");

      const userData = data;

      if (!userData.habilitado) {
        toast.error("Usuario deshabilitado");
        return await internalLogout();
      }

      if (!userData.rol || !Object.values(ROLES).includes(userData.rol)) {
        toast.error("Rol no autorizado");
        return await internalLogout();
      }

      setId(userData.id);
      setCorreo(session.user.email);
      setNombre(userData.nombre || null);
      setApellido(userData.apellido || null);
      setRol(userData.rol);
      setAgencia(userData.agencia || null);
      setClienteId(userData.cliente_id || null);
      setHabilitado(userData.habilitado);
      setEdificiosOk(userData.edificiosOk || null);
      setPrevTokenVersion(userData.token_version || 0);
    } catch (err) {
      console.error("ERROR cargando usuario:", err);
      await internalLogout();
    } finally {
      setUserLoading(false);
    }
  };

  // Setear país (solo una vez al montar)
  useEffect(() => {
    setPais(getPaisFromHostname().nombre);
  }, []);

  // CARGAR DATOS DEL USUARIO SOLO CUANDO CAMBIE EL EMAIL (login/logout real)
  useEffect(() => {
    loadUserData();
  }, [session?.user?.email]);

  // Si hay sesión pero aún no tenemos rol, seguimos en loading
  const stillLoadingUser = session && !rol && !authLoading;

  const value = {
    apellido,
    correo,
    edificiosOk,
    habilitado,
    id,
    nombre,
    pais,
    rol,
    setRol,
    session,
    isLoading: authLoading || userLoading || stillLoadingUser,
    agencia,
    clienteId,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuthContext debe usarse dentro de AuthProvider");
  return context;
};
